import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaCarAlt, FaCalendarAlt } from 'react-icons/fa';
import { FaUserGroup, FaSackDollar } from 'react-icons/fa6';
import { MdOutlineNavigateNext } from 'react-icons/md';
import { GrFormPrevious } from 'react-icons/gr';
import { IoMdArrowRoundForward } from 'react-icons/io';
import RideHistoryTable from '../components/RideHistoryTable';

const Dashboard = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [upcomingRides, setUpcomingRides] = useState(0);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [rideHistory, setRideHistory] = useState([]);
  const [totalExpenditure, setTotalExpenditure] = useState(0);
  const entriesPerPage = 9;
  const totalEntries = rideHistory.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const startEntry = (currentPage - 1) * entriesPerPage + 1;
  const endEntry = Math.min(startEntry + entriesPerPage - 1, totalEntries);

  const fetchEmployeeData = async () => {
    try {
      const authKey = localStorage.getItem('key');
      const companyId = localStorage.getItem('authToken');

      const formData = new FormData();
      formData.append('key', authKey);
      formData.append('cid', companyId);

      const response = await fetch(
        'https://app.mindstreamsoft.com/emp_directory.php',
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        setEmployeeCount(data.length);
      } else {
        console.error('Unexpected API response:', data);
      }
    } catch (error) {
      console.error('Error fetching employee data:', error);
    }
  };

  const fetchRideHistory = async () => {
    try {
      const authKey = localStorage.getItem('key');
      const companyId = localStorage.getItem('authToken');

      const formData = new FormData();
      formData.append('key', authKey);
      formData.append('cid', companyId);

      const response = await fetch(
        'https://app.mindstreamsoft.com/ride_history.php',
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const totalCost = data.reduce(
        (sum, ride) => sum + parseFloat(ride.fare || 0),
        0
      );

      const bookedRides = data.filter(
        (ride) => ride.status === 'Booked'
      ).length;
      const pendingRides = data.filter(
        (ride) => ride.status === 'Pending'
      ).length;
      const upcomingRides = bookedRides + pendingRides;

      setTotalExpenditure(totalCost.toFixed(2));
      setUpcomingRides(upcomingRides);
      setRideHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching ride history:', error);
    }
  };

  useEffect(() => {
    fetchEmployeeData();
    fetchRideHistory();
  }, []);

  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  const filteredRides = rideHistory.filter((ride) =>
    Object.values(ride)
      .join(' ')
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const statsData = [
    {
      title: 'Total Rides',
      stats: rideHistory.length,
      icon: <FaCarAlt />,
    },
    {
      title: 'Active Employees',
      stats: employeeCount,
      icon: <FaUserGroup />,
    },
    {
      title: 'Total Expenditures',
      stats: `€${totalExpenditure}`,
      icon: <FaSackDollar />,
    },
    {
      title: 'Upcoming Rides',
      stats: upcomingRides,
      icon: <FaCalendarAlt />,
    },
  ];

  return (
    <div className='dashboard-container'>
      <div className='content'>
        <div className='d-flex justify-content-start my-2'>
          <Link
            to='/book-ride'
            className='btn glowing-btn'
            style={{
              backgroundColor: '#f37a20',
              color: '#fff',
              textDecoration: 'none',
              borderRadius: '10px',
              width: '190px',
              height: '50px',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 15px rgba(243, 122, 32, 0.6)',
              animation:
                'glow 1.5s infinite alternate ease-in-out, scaleEffect 2s infinite alternate ease-in-out',
            }}
          >
            Book New Ride
            <IoMdArrowRoundForward
              style={{
                marginLeft: '6px',
                fontSize: '22px',
                color: '#fff',
              }}
            />
          </Link>
        </div>

        <div className='row my-2'>
          {statsData.map((item, index) => (
            <div className='col-lg-3 col-md-6 p-2' key={index}>
              <div className='card text-center py-2'>
                <div className='card-body d-flex align-items-center'>
                  <div
                    className='btn rounded-circle d-flex align-items-center justify-content-center'
                    style={{
                      width: '65px',
                      height: '65px',
                      background: '#e8e8e8',
                    }}
                  >
                    <div style={{ color: '#f37a20', fontSize: '28px' }}>
                      {item.icon}
                    </div>
                  </div>
                  <div style={{ textAlign: 'left', marginLeft: '20px' }}>
                    <h6 style={{ color: '#969696', marginBottom: '5px' }}>
                      {item.title}
                    </h6>
                    <h4 style={{ marginBottom: 0 }}>{item.stats}</h4>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className='card'>
          <div className='d-flex justify-content-between align-items-start flex-wrap pt-4 px-4'>
            <div className='d-flex flex-column mb-3 mb-md-0'>
              <h5
                className='mb-1'
                style={{ fontSize: '22px', fontWeight: '600' }}
              >
                Recent Rides
              </h5>
              <p
                className='mt-2'
                style={{
                  color: '#f37a20',
                  fontSize: '14px',
                  fontWeight: '400',
                }}
              >
                This Month
              </p>
            </div>
            <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center'>
              <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center w-100'>
                <input
                  type='text'
                  placeholder='Search'
                  className='form-control mb-3 mb-md-0 flex-grow-1 me-md-3 border-0'
                  style={{
                    maxWidth: '210px',
                    borderRadius: '30px',
                    background: '#f5f7fa',
                    color: '#969696',
                  }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <select
                  className='form-select border-0'
                  style={{
                    maxWidth: '170px',
                    borderRadius: '30px',
                    background: '#f5f7fa',
                    color: '#969696',
                  }}
                >
                  <option value='newest'>Sort by: Newest</option>
                  <option value='oldest'>Sort by: Oldest</option>
                </select>
              </div>
            </div>
          </div>

          <RideHistoryTable
            rides={filteredRides}
            startEntry={startEntry}
            endEntry={endEntry}
          />

          <div className='d-flex justify-content-between align-items-center flex-wrap p-4'>
            <div>
              <span style={{ color: '#969696' }}>
                Showing {startEntry}-{endEntry} of {totalEntries} entries
              </span>
            </div>
            {totalEntries > entriesPerPage && (
              <div>
                <button
                  className='btn btn-link me-2'
                  style={{ borderRadius: '10px', border: '1px solid #969696' }}
                  disabled={currentPage === 1}
                  onClick={handlePrevPage}
                >
                  <GrFormPrevious style={{ fontSize: '20px' }} />
                </button>
                <button
                  className='btn btn-link'
                  style={{ borderRadius: '10px', border: '1px solid #969696' }}
                  disabled={currentPage === totalPages}
                  onClick={handleNextPage}
                >
                  <MdOutlineNavigateNext style={{ fontSize: '20px' }} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
